import { useFlag } from '@/services/featureFlags';
import { useAgentEditorStore } from '@/state/root';
import { TriggerType, TriggerWebhook } from '@d-id/types';
import styled from '@emotion/styled';
import { produce } from 'immer';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { Divider, GeneralContainer, OpaqueTitlePart, RootContainer, SubTitle, Title, TitleContainer } from '../common';
import { New } from '../common/banners/new';
import { WebhookComponent } from '../WebhookComponent';
import CustomGreeting from './CustomGreeting';
import { MaxResponseLength } from './MaxResponseLength';
import { StarterQuestions } from './StarterQuestions';
import { TopicsToAvoid } from './TopicToAvoid';

const WelcomeMessageContainer = styled(GeneralContainer)`
    gap: 16px;
`;

const ConversationStartersSubTitle = styled(SubTitle)`
    margin-bottom: 20px;
`;

const InnerDivider = styled(Divider)`
    margin-bottom: 24px;
`;

const TopicsToAvoidContainer = styled(GeneralContainer)`
    gap: 12px;
`;

export const ChatSettings = observer(() => {
    const { triggers, setTriggers } = useAgentEditorStore();
    const triggersEnabled = useFlag('triggers_enabled', false);

    const handleSaveWebhook = (triggerWebhook: TriggerWebhook | null, index: number) => {
        setTriggers(
            produce(toJS(triggers), draft => {
                if (!draft[TriggerType.chatEnd]) {
                    draft[TriggerType.chatEnd] = {};
                }

                if (!draft[TriggerType.chatEnd].webhooks) {
                    draft[TriggerType.chatEnd].webhooks = [];
                }

                // webhook remove
                if (!triggerWebhook) {
                    draft[TriggerType.chatEnd].webhooks.splice(index, 1);
                    return;
                }

                // webhook add
                if (index === -1) {
                    draft[TriggerType.chatEnd].webhooks.push(triggerWebhook);
                    return;
                }

                // webhook update
                draft[TriggerType.chatEnd].webhooks[index] = triggerWebhook;
            })
        );
    };

    return (
        <RootContainer>
            <WelcomeMessageContainer>
                <TitleContainer>
                    <Title>Welcome message</Title>
                    <SubTitle>Set a friendly message to greet users when they first interact with your avatar</SubTitle>
                </TitleContainer>
                <CustomGreeting />
            </WelcomeMessageContainer>

            <GeneralContainer>
                <TitleContainer>
                    <Title>
                        Conversation starters <OpaqueTitlePart>(Optional)</OpaqueTitlePart>
                    </Title>
                    <ConversationStartersSubTitle>
                        Add up to 4 starter options to help your user start the conversation
                    </ConversationStartersSubTitle>
                </TitleContainer>
                <InnerDivider />
                <StarterQuestions />
            </GeneralContainer>

            <GeneralContainer>
                <InnerDivider />
                <TopicsToAvoidContainer>
                    <Title>
                        Topics to avoid <OpaqueTitlePart>(Optional)</OpaqueTitlePart> <New />
                    </Title>
                    <TopicsToAvoid />
                </TopicsToAvoidContainer>
            </GeneralContainer>

            <GeneralContainer>
                <MaxResponseLength />
            </GeneralContainer>

            {triggersEnabled && <WebhookComponent onSave={handleSaveWebhook} webhooks={triggers?.[TriggerType.chatEnd]?.webhooks || []} />}
        </RootContainer>
    );
});
